import toast from './toast';
import {
  ALIGN,
  COMPANY_NAME,
  DEFAULT_TABLE_STYLES,
  STANDARD_MARGIN,
  createBrandedPDF,
  drawSeparatorLine,
  addBrandedFooter,
  getGeneratedTimestamp,
  fmtCurrency,
  fmtDays,
  fmtDate,
} from './pdfReportTemplate';

const REPORT_TITLE = 'Unutilised Annual Leave Balance';

function buildFilenameBase(cutoffDate) {
  const d = cutoffDate.replace(/-/g, '');
  return `UnutilisedLeave_${d}`;
}

function fmtEntitlementDays(value) {
  if (value == null || value === '') return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '-';
  return Number.isInteger(num) ? String(num) : num.toFixed(2);
}

// ─── Shared column config ────────────────────────────────────────────────────

const COLUMNS = [
  // Zone 1 — Identity
  { key: 'staff_id',              label: 'Staff ID',       halign: ALIGN.TEXT },
  { key: 'employee_name',         label: 'Name',           halign: ALIGN.TEXT },
  { key: 'department',            label: 'Dept',           halign: ALIGN.TEXT },
  // Zone 2 — Employment
  { key: 'employment_start_date', label: 'Start Date',     halign: ALIGN.DATE },
  { key: 'employment_end_date',   label: 'End Date',       halign: ALIGN.DATE },
  { key: 'service_length',        label: 'Service',        halign: ALIGN.TEXT },
  // Zone 3 — Leave narrative
  { key: 'al_entitlement',        label: 'Entitlement',    halign: ALIGN.NUMBER, fmt: fmtEntitlementDays },
  { key: 'carry_forward',         label: 'C/F',            halign: ALIGN.NUMBER, fmt: fmtDays },
  { key: 'accrued_ytd',           label: 'Accrued',        halign: ALIGN.NUMBER, fmt: fmtDays },
  { key: 'al_consumed_ytd',       label: 'Taken (YTD)',    halign: ALIGN.NUMBER, fmt: fmtDays },
  { key: 'al_consumed_month',     label: 'Taken (Month)',  halign: ALIGN.NUMBER, fmt: fmtDays },
  { key: 'unutilised_balance',    label: 'Balance',        halign: ALIGN.NUMBER, fmt: fmtDays },
  // Zone 4 — Financial impact
  { key: 'work_schedule',         label: 'Schedule',       halign: ALIGN.STATUS },
  { key: 'basic_salary',          label: 'Basic Salary',   halign: ALIGN.CURRENCY, fmt: fmtCurrency },
  { key: 'daily_rate',            label: 'Gross Daily Rate', halign: ALIGN.CURRENCY, fmt: fmtCurrency },
  { key: 'monetary_liability',    label: 'Liability ($)',  halign: ALIGN.CURRENCY, fmt: fmtCurrency },
];

function rowToArray(row) {
  return COLUMNS.map((col) => {
    const raw = row[col.key];
    if (raw == null) return '-';
    return col.fmt ? col.fmt(raw) : raw;
  });
}

function sumField(rows, key) {
  return rows.reduce((s, r) => s + (r[key] ? parseFloat(r[key]) : 0), 0);
}

// ─── PDF Export ──────────────────────────────────────────────────────────────

export async function exportUnutilisedLeavePDF(reportData) {
  const cutoff = reportData.cutoff_date;
  const staffRows = reportData.employees?.staff || [];
  const workerRows = reportData.employees?.worker || [];
  const summary = reportData.summary;

  // ── Cover page ──
  const cover = await createBrandedPDF({
    title: REPORT_TITLE,
    subtitle: `Cutoff Date: ${fmtDate(cutoff)}`,
    orientation: 'landscape',
  });
  const { doc, autoTable: autoTableFn } = cover;

  let y = cover.headerEndY + 4;

  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.text('Report Summary', cover.leftX, y);
  y += 6;

  const summaryTableBody = [
    ['Staff', String(summary.staff.count), fmtDays(summary.staff.total_unutilised_days), fmtCurrency(summary.staff.total_monetary_liability)],
    ['Worker', String(summary.worker.count), fmtDays(summary.worker.total_unutilised_days), fmtCurrency(summary.worker.total_monetary_liability)],
  ];
  const summaryTotalRow = [
    { content: 'Grand Total', styles: { fontStyle: 'bold' } },
    { content: String(summary.grand.count), styles: { fontStyle: 'bold' } },
    { content: fmtDays(summary.grand.total_unutilised_days), styles: { fontStyle: 'bold' } },
    { content: fmtCurrency(summary.grand.total_monetary_liability), styles: { fontStyle: 'bold' } },
  ];
  summaryTableBody.push(summaryTotalRow);

  autoTableFn(doc, {
    startY: y,
    head: [['Classification', 'Employees', 'Unutilised Days', 'Monetary Liability']],
    body: summaryTableBody,
    ...DEFAULT_TABLE_STYLES,
    margin: cover.tableMargin,
    tableWidth: 180,
    columnStyles: {
      0: { halign: ALIGN.TEXT },
      1: { halign: ALIGN.NUMBER },
      2: { halign: ALIGN.NUMBER },
      3: { halign: ALIGN.CURRENCY },
    },
  });

  y = doc.lastAutoTable.finalY + 10;

  // Department breakdown
  const deptBreakdown = reportData.department_breakdown || [];
  if (deptBreakdown.length > 0) {
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('Department Breakdown', cover.leftX, y);
    y += 6;

    const deptBody = deptBreakdown.map((d) => [
      d.department,
      String(d.total.count),
      fmtDays(d.total.total_unutilised_days),
      fmtCurrency(d.total.total_monetary_liability),
    ]);

    autoTableFn(doc, {
      startY: y,
      head: [['Department', 'Employees', 'Unutilised Days', 'Monetary Liability']],
      body: deptBody,
      ...DEFAULT_TABLE_STYLES,
      margin: cover.tableMargin,
      tableWidth: 180,
      columnStyles: {
        0: { halign: ALIGN.TEXT },
        1: { halign: ALIGN.NUMBER },
        2: { halign: ALIGN.NUMBER },
        3: { halign: ALIGN.CURRENCY },
      },
    });
  }

  // ── Detail pages (grouped by classification) ──

  const drawSection = (title, rows) => {
    if (rows.length === 0) return;

    doc.addPage('landscape');
    const leftX = STANDARD_MARGIN;
    const rightX = doc.internal.pageSize.getWidth() - STANDARD_MARGIN;
    let sectionY = 14;

    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(title, leftX, sectionY);
    sectionY += 6;
    drawSeparatorLine(doc, sectionY, leftX, rightX);

    const bodyData = rows.map(rowToArray);

    const subUnutilised = sumField(rows, 'unutilised_balance');
    const subLiability = sumField(rows, 'monetary_liability');

    bodyData.push([
      { content: `Sub-total (${rows.length})`, colSpan: 11, styles: { fontStyle: 'bold', halign: 'right' } },
      { content: fmtDays(subUnutilised), styles: { fontStyle: 'bold' } },
      '',
      '',
      '',
      { content: fmtCurrency(subLiability), styles: { fontStyle: 'bold' } },
    ]);

    const colStyles = {};
    COLUMNS.forEach((col, i) => { colStyles[i] = { halign: col.halign }; });

    autoTableFn(doc, {
      startY: sectionY,
      head: [COLUMNS.map((c) => c.label)],
      body: bodyData,
      ...DEFAULT_TABLE_STYLES,
      margin: cover.tableMargin,
      columnStyles: colStyles,
      didParseCell: (data) => {
        if (data.section !== 'body') return;
        if (data.row.index === bodyData.length - 1) {
          data.cell.styles.fillColor = [243, 244, 246];
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });
  };

  drawSection('Staff', staffRows);
  drawSection('Worker', workerRows);

  // Grand total page element
  if (staffRows.length > 0 && workerRows.length > 0) {
    const allRows = [...staffRows, ...workerRows];
    const grandUnutilised = sumField(allRows, 'unutilised_balance');
    const grandLiability = sumField(allRows, 'monetary_liability');
    const finalY = doc.lastAutoTable?.finalY || doc.internal.pageSize.getHeight() - 30;

    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.text(
      `Grand Total — ${allRows.length} employees | Unutilised: ${fmtDays(grandUnutilised)} days | Liability: ${fmtCurrency(grandLiability)}`,
      STANDARD_MARGIN,
      finalY + 8,
    );
  }

  addBrandedFooter(doc, cover);

  const filename = `${buildFilenameBase(cutoff)}.pdf`;
  doc.save(filename);
  toast.success('PDF downloaded');
}

// ─── Excel Styles (aligned with backend openpyxl payroll register aesthetic) ─

const _BRAND_NAVY = '1B2A4A';
const _WHITE = 'FFFFFF';
const _GRAY_50 = 'FAFAFA';
const _GRAY_200 = 'E5E7EB';
const _GRAY_700 = '374151';
const _GREEN_LIGHT = 'E2EFDA';

const THIN_BORDER = {
  top:    { style: 'thin', color: { rgb: _GRAY_200 } },
  bottom: { style: 'thin', color: { rgb: _GRAY_200 } },
  left:   { style: 'thin', color: { rgb: _GRAY_200 } },
  right:  { style: 'thin', color: { rgb: _GRAY_200 } },
};

const TOTAL_BORDER = {
  top:    { style: 'double', color: { rgb: _BRAND_NAVY } },
  bottom: { style: 'thin', color: { rgb: _GRAY_200 } },
  left:   { style: 'thin', color: { rgb: _GRAY_200 } },
  right:  { style: 'thin', color: { rgb: _GRAY_200 } },
};

const STYLE = {
  title:     { font: { name: 'Calibri', sz: 13, bold: true, color: { rgb: _BRAND_NAVY } } },
  subtitle:  { font: { name: 'Calibri', sz: 9, italic: true, color: { rgb: _GRAY_700 } } },
  metaLabel: { font: { name: 'Calibri', sz: 9, bold: true, color: { rgb: _GRAY_700 } } },
  metaValue: { font: { name: 'Calibri', sz: 9, color: { rgb: '111827' } } },
  sectionHdr:{ font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: _BRAND_NAVY } } },
  footer:    { font: { name: 'Calibri', sz: 8, italic: true, color: { rgb: '9CA3AF' } } },
};

function headerStyle(halign = 'center') {
  return {
    font: { name: 'Calibri', sz: 9, bold: true, color: { rgb: _WHITE } },
    fill: { patternType: 'solid', fgColor: { rgb: _BRAND_NAVY } },
    alignment: { horizontal: halign, vertical: 'center', wrapText: true },
    border: THIN_BORDER,
  };
}

function dataStyle(halign, isEven, numFmt) {
  const s = {
    font: { name: 'Calibri', sz: 9, color: { rgb: '111827' } },
    fill: { patternType: 'solid', fgColor: { rgb: isEven ? _GRAY_50 : _WHITE } },
    alignment: { horizontal: halign || 'left', vertical: 'center' },
    border: THIN_BORDER,
  };
  if (numFmt) s.numFmt = numFmt;
  return s;
}

function totalStyle(halign, numFmt) {
  const s = {
    font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: _BRAND_NAVY } },
    fill: { patternType: 'solid', fgColor: { rgb: _GREEN_LIGHT } },
    alignment: { horizontal: halign || 'right', vertical: 'center' },
    border: TOTAL_BORDER,
  };
  if (numFmt) s.numFmt = numFmt;
  return s;
}

function numOrBlank(v) {
  if (v == null || v === '') return '';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return isNaN(n) ? '' : n;
}

let _xlsxStyleMod = null;

async function loadXlsxStyle() {
  if (!_xlsxStyleMod) {
    const mod = await import('xlsx-js-style');
    _xlsxStyleMod = mod.default || mod;
  }
  return _xlsxStyleMod;
}

// ─── Excel Export (2-sheet, branded) ─────────────────────────────────────────

export async function exportUnutilisedLeaveExcel(reportData) {
  const XL = await loadXlsxStyle();

  const cutoff = reportData.cutoff_date;
  const summary = reportData.summary;
  const deptBreakdown = reportData.department_breakdown || [];
  const staffRows = reportData.employees?.staff || [];
  const workerRows = reportData.employees?.worker || [];

  const wb = XL.utils.book_new();

  // ── Sheet 1: Summary ────────────────────────────────────────────────────

  const summaryAoa = [];
  let r = 0;

  // Title block
  summaryAoa.push([COMPANY_NAME]);                  // r=0
  summaryAoa.push([REPORT_TITLE]);                  // r=1
  summaryAoa.push([]);                              // r=2
  summaryAoa.push(['Cutoff Date', fmtDate(cutoff)]);// r=3
  summaryAoa.push(['Generated', getGeneratedTimestamp()]); // r=4
  summaryAoa.push([]);                              // r=5
  r = 6;

  // Classification summary header
  const classHdrs = ['Classification', 'Employees', 'Unutilised Days', 'Monetary Liability'];
  summaryAoa.push(classHdrs);                       // r=6
  summaryAoa.push(['Staff', Number(summary.staff.count), numOrBlank(summary.staff.total_unutilised_days), numOrBlank(summary.staff.total_monetary_liability)]);
  summaryAoa.push(['Worker', Number(summary.worker.count), numOrBlank(summary.worker.total_unutilised_days), numOrBlank(summary.worker.total_monetary_liability)]);
  summaryAoa.push(['Grand Total', Number(summary.grand.count), numOrBlank(summary.grand.total_unutilised_days), numOrBlank(summary.grand.total_monetary_liability)]);
  summaryAoa.push([]);                              // r=10
  r = 11;

  // Department breakdown
  const deptHeaderRow = r;
  if (deptBreakdown.length > 0) {
    summaryAoa.push(['Department Breakdown']);       // r=11
    summaryAoa.push(['Department', 'Employees', 'Unutilised Days', 'Monetary Liability']); // r=12
    r = 13;
    deptBreakdown.forEach((d) => {
      summaryAoa.push([
        d.department,
        Number(d.total.count),
        numOrBlank(d.total.total_unutilised_days),
        numOrBlank(d.total.total_monetary_liability),
      ]);
      r++;
    });
    summaryAoa.push([]);
    r++;
  }

  // Footer
  const footerText = `This is a computer-generated report. Generated by WEPL HR Portal on ${getGeneratedTimestamp()}. Confidential — For authorised use only.`;
  summaryAoa.push([footerText]);
  const summaryFooterRow = summaryAoa.length - 1;

  const ws1 = XL.utils.aoa_to_sheet(summaryAoa);

  // Column widths
  ws1['!cols'] = [{ wch: 28 }, { wch: 16 }, { wch: 20 }, { wch: 22 }];

  // Merged cells
  ws1['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 3 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 3 } },
    { s: { r: summaryFooterRow, c: 0 }, e: { r: summaryFooterRow, c: 3 } },
  ];

  // Apply styles — title block
  ws1['A1'].s = STYLE.title;
  ws1['A2'].s = STYLE.subtitle;
  ws1['A4'].s = STYLE.metaLabel;
  ws1['B4'].s = STYLE.metaValue;
  ws1['A5'].s = STYLE.metaLabel;
  ws1['B5'].s = STYLE.metaValue;

  // Classification table header (row index 6 = Excel row 7)
  const classAligns = ['left', 'right', 'right', 'right'];
  const classFmts  = [null, null, '#,##0.00', '#,##0.00'];
  for (let c = 0; c < 4; c++) {
    const ref = XL.utils.encode_cell({ r: 6, c });
    if (ws1[ref]) ws1[ref].s = headerStyle(classAligns[c]);
  }

  // Staff row (r=7), Worker row (r=8)
  [7, 8].forEach((rowIdx, di) => {
    for (let c = 0; c < 4; c++) {
      const ref = XL.utils.encode_cell({ r: rowIdx, c });
      if (ws1[ref]) ws1[ref].s = dataStyle(classAligns[c], di % 2 === 0, classFmts[c]);
    }
  });

  // Grand total row (r=9)
  for (let c = 0; c < 4; c++) {
    const ref = XL.utils.encode_cell({ r: 9, c });
    if (ws1[ref]) ws1[ref].s = totalStyle(c === 0 ? 'left' : 'right', classFmts[c]);
  }

  // Department breakdown styles
  if (deptBreakdown.length > 0) {
    const sectionRow = deptHeaderRow;
    const deptHdrRef = XL.utils.encode_cell({ r: sectionRow, c: 0 });
    if (ws1[deptHdrRef]) ws1[deptHdrRef].s = STYLE.sectionHdr;

    for (let c = 0; c < 4; c++) {
      const ref = XL.utils.encode_cell({ r: sectionRow + 1, c });
      if (ws1[ref]) ws1[ref].s = headerStyle(classAligns[c]);
    }

    deptBreakdown.forEach((_, di) => {
      const rowIdx = sectionRow + 2 + di;
      for (let c = 0; c < 4; c++) {
        const ref = XL.utils.encode_cell({ r: rowIdx, c });
        if (ws1[ref]) ws1[ref].s = dataStyle(classAligns[c], di % 2 === 0, classFmts[c]);
      }
    });
  }

  // Footer style
  const footerRef = XL.utils.encode_cell({ r: summaryFooterRow, c: 0 });
  if (ws1[footerRef]) ws1[footerRef].s = STYLE.footer;

  XL.utils.book_append_sheet(wb, ws1, 'Summary');

  // ── Sheet 2: Detail ─────────────────────────────────────────────────────

  const detailColDefs = [
    { header: 'Classification', width: 14, align: 'left' },
    { header: 'Staff ID',       width: 12, align: 'left' },
    { header: 'Name',           width: 24, align: 'left' },
    { header: 'Dept',           width: 18, align: 'left' },
    { header: 'Start Date',     width: 13, align: 'center' },
    { header: 'End Date',       width: 13, align: 'center' },
    { header: 'Service',        width: 12, align: 'left' },
    { header: 'Entitlement',    width: 13, align: 'right', numFmt: '#,##0.00' },
    { header: 'C/F',            width: 10, align: 'right', numFmt: '#,##0.00' },
    { header: 'Accrued',        width: 11, align: 'right', numFmt: '#,##0.00' },
    { header: 'Taken (YTD)',    width: 13, align: 'right', numFmt: '#,##0.00' },
    { header: 'Taken (Month)',  width: 14, align: 'right', numFmt: '#,##0.00' },
    { header: 'Balance',        width: 11, align: 'right', numFmt: '#,##0.00' },
    { header: 'Schedule',       width: 12, align: 'center' },
    { header: 'Basic Salary',   width: 15, align: 'right', numFmt: '#,##0.00' },
    { header: 'Gross Daily Rate', width: 16, align: 'right', numFmt: '#,##0.00' },
    { header: 'Liability ($)',  width: 16, align: 'right', numFmt: '#,##0.00' },
  ];

  const detailAoa = [];

  // Header row
  detailAoa.push(detailColDefs.map((d) => d.header));

  let dataIdx = 0;
  const dataRowMeta = [];

  const addGroup = (label, rows) => {
    rows.forEach((emp) => {
      detailAoa.push([
        label,
        emp.staff_id || '',
        emp.employee_name || '',
        emp.department || '',
        emp.employment_start_date || '',
        emp.employment_end_date || '',
        emp.service_length || '',
        numOrBlank(emp.al_entitlement),
        numOrBlank(emp.carry_forward),
        numOrBlank(emp.accrued_ytd),
        numOrBlank(emp.al_consumed_ytd ?? emp.al_consumed),
        numOrBlank(emp.al_consumed_month),
        numOrBlank(emp.unutilised_balance),
        emp.work_schedule || '',
        numOrBlank(emp.basic_salary),
        numOrBlank(emp.daily_rate),
        numOrBlank(emp.monetary_liability),
      ]);
      dataRowMeta.push({ isEven: dataIdx % 2 === 0, isTotal: false });
      dataIdx++;
    });
  };

  addGroup('Staff', staffRows);
  addGroup('Worker', workerRows);

  // Grand total row
  const allRows = [...staffRows, ...workerRows];
  if (allRows.length > 0) {
    const totalArr = new Array(detailColDefs.length).fill('');
    totalArr[0] = `Grand Total (${allRows.length})`;
    totalArr[12] = numOrBlank(sumField(allRows, 'unutilised_balance'));
    totalArr[16] = numOrBlank(sumField(allRows, 'monetary_liability'));
    detailAoa.push(totalArr);
    dataRowMeta.push({ isEven: false, isTotal: true });
  }

  // Footer
  detailAoa.push([]);
  const detailFooterArr = new Array(detailColDefs.length).fill('');
  detailFooterArr[0] = footerText;
  detailAoa.push(detailFooterArr);

  const ws2 = XL.utils.aoa_to_sheet(detailAoa);

  // Column widths
  ws2['!cols'] = detailColDefs.map((d) => ({ wch: d.width }));

  // Merge footer row across all columns
  const detailFooterRowIdx = detailAoa.length - 1;
  ws2['!merges'] = [
    { s: { r: detailFooterRowIdx, c: 0 }, e: { r: detailFooterRowIdx, c: detailColDefs.length - 1 } },
  ];

  // Auto-filter on header row
  ws2['!autofilter'] = {
    ref: `A1:${XL.utils.encode_col(detailColDefs.length - 1)}1`,
  };

  // Style header row (row 0)
  for (let c = 0; c < detailColDefs.length; c++) {
    const ref = XL.utils.encode_cell({ r: 0, c });
    if (ws2[ref]) ws2[ref].s = headerStyle(detailColDefs[c].align);
  }

  // Style data rows
  dataRowMeta.forEach((meta, di) => {
    const rowIdx = di + 1;
    for (let c = 0; c < detailColDefs.length; c++) {
      const ref = XL.utils.encode_cell({ r: rowIdx, c });
      if (!ws2[ref]) ws2[ref] = { v: '', t: 's' };
      if (meta.isTotal) {
        ws2[ref].s = totalStyle(c === 0 ? 'left' : 'right', detailColDefs[c].numFmt);
      } else {
        ws2[ref].s = dataStyle(detailColDefs[c].align, meta.isEven, detailColDefs[c].numFmt);
      }
    }
  });

  // Footer style
  const dfRef = XL.utils.encode_cell({ r: detailFooterRowIdx, c: 0 });
  if (ws2[dfRef]) ws2[dfRef].s = STYLE.footer;

  XL.utils.book_append_sheet(wb, ws2, 'Detail');

  // ── Write ───────────────────────────────────────────────────────────────

  const filename = `${buildFilenameBase(cutoff)}.xlsx`;
  XL.writeFile(wb, filename);
  toast.success('Excel downloaded');
}

// ─── CSV Export (detail-only, flat) ──────────────────────────────────────────

export function exportUnutilisedLeaveCSV(reportData) {
  const staffRows = reportData.employees?.staff || [];
  const workerRows = reportData.employees?.worker || [];
  const allRows = [...staffRows, ...workerRows];

  const headers = ['Classification', ...COLUMNS.map((c) => c.label)];

  const csvRows = allRows.map((r) => {
    const cells = COLUMNS.map((col) => {
      const v = r[col.key];
      if (v == null) return '';
      return String(v);
    });
    cells.unshift(r.classification);
    return cells.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',');
  });

  const csv = [headers.join(','), ...csvRows].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${buildFilenameBase(reportData.cutoff_date)}.csv`;
  a.click();
  URL.revokeObjectURL(url);

  toast.success('CSV downloaded');
}
